/**
 * DemoModeToggle - Demo Version
 *
 * In the demo deployment, this toggle is always enabled and disabled.
 * It provides visual consistency with the production application but
 * cannot be changed. Demo mode is permanently enabled.
 */

import { DEMO_CLIENT } from '@/hooks/useDemoMode';
import { cn } from '@/lib/utils';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Presentation } from 'lucide-react';

interface DemoModeToggleProps {
  className?: string;
}

export function DemoModeToggle({ className }: DemoModeToggleProps) {
  // Demo mode is always enabled in the demo deployment
  return (
    <div className={cn('space-y-3', className)}>
      <div className="flex items-center gap-2 px-2 text-sm font-medium text-muted-foreground">
        <Presentation className="h-4 w-4" />
        <span>Demo Mode</span>
      </div>
      <Label
        htmlFor="demo-mode-toggle"
        className={cn(
          'flex items-center justify-between gap-3 rounded-lg border p-3 transition-all',
          'border-primary bg-primary/5',
          'cursor-not-allowed opacity-75',
        )}
      >
        <div className="flex-1 select-none">
          <span className="font-medium block text-foreground">Enabled</span>
          <p className="text-xs text-muted-foreground font-normal">
            Queries scoped to {DEMO_CLIENT}
          </p>
        </div>
        <Switch
          id="demo-mode-toggle"
          checked={true}
          disabled={true}
          aria-describedby="demo-mode-description"
        />
      </Label>
      <p id="demo-mode-description" className="sr-only">
        Demo mode is permanently enabled. All chat queries are filtered to show
        only results from {DEMO_CLIENT}.
      </p>
    </div>
  );
}
